import { wandler } from '../../../../utils/apiTypes';
import { wandlerState } from './wandlerTypes';
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { getWithAuth, putWithAuth, deleteWithAuth } from "../../../../utils/api";
import type { RootState, AppDispatch } from "../../../../app/store";
import { logError } from "../../../../utils/utils";
import { openSnackbar } from "../../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../../utils/errorHandling';

export const wandlerDummy: wandler
  = {
  faktor: "",
  kommentar: "",
  zugeordnet: false
};

const initialState: wandlerState
  = {
  wandlerListe: [],
  alterWandler: wandlerDummy,
  aktuellerWandler: wandlerDummy,
  newEntry: false,
  editEntry: false,
  editDirty: 0,
  viewMode: false,
  error: [],
  listLoading: false,
  sorting: [
    {
      field: 'faktor',
      sort: 'asc',
    },
  ]
};

const wandlerSlice = createSlice({
  name: "wandler",
  initialState,
  reducers:
  {
    updateWandlerListe: (state, action: PayloadAction<wandler>) => {
      const updateIndex = state.wandlerListe.findIndex(
        (x: wandler) => x.faktor == action.payload.faktor
      );
      const firstPart = state.wandlerListe.slice(0, updateIndex);
      let lastPart = Array<wandler>();
      if (updateIndex + 1 < state.wandlerListe.length)
        lastPart = state.wandlerListe.slice(updateIndex + 1);
      state.wandlerListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.faktor.localeCompare(b.faktor));
    },
    removeFromWandlerListe: (state, action: PayloadAction<wandler>) => {
      const updateIndex = state.wandlerListe.findIndex(
        (x) => x.faktor == action.payload.faktor
      );
      const firstPart = state.wandlerListe.slice(0, updateIndex);
      let lastPart = Array<wandler>();
      if (updateIndex + 1 < state.wandlerListe.length)
        lastPart = state.wandlerListe.slice(updateIndex + 1);
      state.wandlerListe = [...firstPart, ...lastPart];
    },
    addAktuellerWandlerToWandlerListe: (state) => {
      const wandlerArray = state.wandlerListe;
      wandlerArray.push(state.aktuellerWandler);
      state.wandlerListe = [...wandlerArray].sort((a, b) => a.faktor.localeCompare(b.faktor));
    },
    setWandlerListe: (state, action: PayloadAction<Array<wandler>>) => {
      state.wandlerListe = action.payload;
    },
    setAktuellerWandler: (state, action: PayloadAction<wandler>) => {
      state.aktuellerWandler = action.payload;
      state.alterWandler = action.payload;
    },
    setAlterWandler: (state, action: PayloadAction<wandler>) => {
      state.alterWandler = action.payload;
    },
    resetAktuellerWandler: (state) => {
      state.aktuellerWandler = state.alterWandler;
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = 0;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setFaktor: (state, action: PayloadAction<string>) => {
      state.aktuellerWandler.faktor = action.payload;
      state.editDirty =
        state.aktuellerWandler.faktor ==
          state.alterWandler.faktor
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuellerWandler.kommentar = action.payload;
      state.editDirty =
        state.aktuellerWandler.kommentar ==
          state.alterWandler.kommentar
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  }
});


//Selectors
//======================================================================
export const selectWandlerListe = (state: RootState): Array<wandler> =>
  state.wandler.wandlerListe;
export const selectAktuellerWandler = (state: RootState): wandler =>
  state.wandler.aktuellerWandler;
export const selectEditEntry = (state: RootState): boolean =>
  state.wandler.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.wandler.newEntry;
export const selectEditDirty = (state: RootState): number =>
  state.wandler.editDirty;
export const selectViewMode = (state: RootState): boolean => state.wandler.viewMode;
export const selectError = (state: RootState): boolean => state.wandler.error.length > 0;
export const selectListLoading = (state: RootState): boolean => state.wandler.listLoading;
export const selectSorting = (state: RootState): GridSortModel => state.wandler.sorting;

//Actions
//=====================================================================

export const getWandlerListe = (url: string, typ: string) => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth(url, {})
    .then((response) => {
      dispatch(setWandlerListe(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const updateWandler = (aktuellerWandler: wandler, url: string, typ: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth(url , {}, aktuellerWandler, aktuellerWandler.faktor)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `${typ}wandler mit Faktor ${aktuellerWandler.faktor} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(setAlterWandler(aktuellerWandler));
      dispatch(updateWandlerListe(aktuellerWandler));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));

};

export const updateAktuellerWandler = (aktuellerWandler: wandler): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setAktuellerWandler(aktuellerWandler));
  dispatch(setViewMode());
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellerWandler());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellerWandler());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuellerWandler(wandlerDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};

export const createWandler = (aktuellerWandler: wandler, url: string, typ: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  putWithAuth(url , {}, aktuellerWandler, aktuellerWandler.faktor)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `${typ}wandler mit Faktor ${aktuellerWandler.faktor} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(addAktuellerWandlerToWandlerListe());
      dispatch(setAktuellerWandler(wandlerDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);

    }).finally(() => dispatch(setListLoading(false)));

};

export const deleteWandler = (aktuellerWandler: wandler, url: string): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setListLoading(true));
  deleteWithAuth(url, {}, aktuellerWandler.faktor)
    .then(() => {
      dispatch(setAktuellerWandler(wandlerDummy));
      dispatch(removeFromWandlerListe(aktuellerWandler));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(openSnackbar({
        message: `${aktuellerWandler.faktor} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      }));
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch)
    }).finally(() => dispatch(setListLoading(false)));
};

export const {
  removeFromWandlerListe,
  setWandlerListe,
  setAktuellerWandler,
  resetAktuellerWandler,
  setAlterWandler,
  updateWandlerListe,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  setError,
  resetError,
  setFaktor,
  setKommentar,
  addAktuellerWandlerToWandlerListe,
  setListLoading,
  setSorting
} = wandlerSlice.actions;
export default wandlerSlice.reducer;